<template>
  <div class="container d-flex flex-column min-vh-100 mb-0"> <!-- Fixed min-vh-80 to min-vh-100 for full height -->
    <div class="row justify-content-center flex-grow-1 align-items-center"> <!-- flex-grow-1 to push footer down; align-items-center for vertical centering -->
      <div class="col-10 col-md-7 col-lg-5 col-xl-4 mx-auto">
        <div id="card-black" class="card mt-0"> <!-- mt-0 to reduce top space -->
          <div id="card-black" class="card-header p-0 overflow-hidden">
            <div class="header-container d-flex justify-content-center align-items-center">
              <img :src="brandLogo" :alt="brandName + ' Logo'" class="logo-image" :class="brandLogoClass" />
            </div>
          </div>
          <div class="card-body">
            <Form role="form" class="text-start mt-3" :validation-schema="schema" @submit="handleLogin">
              <div class="mb-3">
                <material-input-field id="username" v-model="user.username" type="text" label="Username"
                  name="username" variant="static" autocomplete="username" autocapitalize="none"
                  autocorrect="off" spellcheck="false" />
              </div>
              <div class="mb-3">
                <material-input-field id="password" v-model="user.password" type="password" label="Password"
                  name="password" variant="static" autocomplete="current-password" />
              </div>
              <div class="text-center">
                <material-button class="my-4 mb-2" variant="gradient" :color="brandButtonColor" full-width type="submit">
                  <span>Sign in</span>
                </material-button>
              </div>
            </Form>
          </div>
          <div class="card-footer text-center pt-0 px-sm-4 px-1 mt-4">
            <p class="mb-2 mx-auto">
              <router-link :to="{ name: 'PasswordForgot' }" class="text-white font-weight-bold">Forgot
                Password</router-link>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <app-footer />
</template>

<script>
import branding from "../branding";
import config from "../config"
import authService from "../services/authService"
import axios from 'axios';
import Cookies from 'js-cookie';
import { markSessionActive } from '../services/tokenService';
import api from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";
import { getDefaultAppScreen } from "./Settings/customerConfigHelper";
import { jwtDecode } from "jwt-decode";
import i18n from '../i18n';
import AppFooter from "@/examples/Footer.vue";
import MaterialInputField from "@/components/MaterialInputField.vue";
import MaterialButton from "@/components/MaterialButton.vue";
import { mapMutations } from "vuex";
import { Form } from "vee-validate"
import * as Yup from 'yup'
const body = document.getElementsByTagName("body")[0];

export default {
  name: "Login",
  components: {
      AppFooter,
      MaterialInputField,
      MaterialButton,
      Form,
  },
  data() {
      return {
          brandLogo: branding.loginLogo,
          brandName: branding.name,
          brandLogoClass: branding.loginLogoClass,
          brandButtonColor: branding.buttonColor,
          user: {username: "", password: ""},
          schema: Yup.object().shape({
              username: Yup.string().required("Username is a required input"), // Changed from 'email' to 'username'; removed email() if not needed
              password: Yup.string().required("Password is a required input").min(5, "Password must be at least 5 characters")
          }),
      };
  },
  beforeMount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
    body.classList.remove("bg-gray-100");
  },
  beforeUnmount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
    body.classList.add("bg-gray-100");
  },
  methods: {
    ...mapMutations(["toggleEveryDisplay", "toggleHideConfig"]),
    async handleLogin() {
        try {
        const response = await axios.post(config.apiUrl + '/identity/loginUser', {
          username: this.user.username, // Fixed: Use this.user.username (matches data and form)
          password: this.user.password
        }, { withCredentials: true });

        if (response.status === 200) {
          Cookies.set('accessToken', response.data.accessToken, { secure: config.secureCookies, expires: 7 });
          // Refresh token is now set by the server as an HttpOnly cookie; clear any legacy JS copy.
          Cookies.remove('refreshToken');
          markSessionActive();
          authService.login();
          await this.setLocale(response.data.accessToken);
          this.$router.push(await getDefaultAppScreen());

        } else {
          this.$toast.error(this.$t('common.loginfail'))
        }
      } catch (error) {
        let message = !error.response
          ? 'Network error - unable to reach server'
          : getApiErrorMessage(this.$t, error);

        authService.logout();
        console.error(error); // eslint-disable-line no-console
        alert(message)
      }
    },
    async setLocale(accessToken) {
      const decodedToken = jwtDecode(accessToken);
      const userUrl = `/User/${decodedToken.UserIdentifier}`;
      api.get(userUrl)
        .then(response => {
          // Map the language from the API response to the locale in VueI18n
          Cookies.set('locale',response.data.language,{ secure: config.secureCookies, httpOnly: false })
          switch (response.data.language) {
            case 'en-US':
              i18n.locale = 'en-US';
              break;
            case 'pl-PL':
              i18n.locale = 'pl-PL';
              break;
            case 'is-IS':
              i18n.locale = 'is-IS';
              break;
            case 'lt-LT':
              i18n.locale = 'lt-LT';
              break;
            case 'es-ES':
              i18n.locale = 'es-ES';
              break;
            case 'ro-RO':
              i18n.locale = 'ro-RO';
              break;
            case 'sk-SK':
              i18n.locale = 'sk-SK';
              break;
            case 'sl-SI':
              i18n.locale = 'sl-SI';
              break;
            default:
              i18n.locale = 'en-US';
          }
        })
        .catch(error => {
          console.error('Failed to fetch user locale on startup:', error); // eslint-disable-line no-console
        });
    }
  },
};
</script>
<style scoped>
  #card-black {
    background: #27293d;
  }

  .container {
    padding-top: 0;
    padding-bottom: 0;
  }

  /* Header container for logo */
  .header-container {
    min-height: 200px;
    background-color: transparent;
    padding: 24px 20px;
    overflow: hidden;
  }

  /* Logo: fit within card */
  .logo-image {
    width: 80%;
    max-width: 360px;
    height: auto;
    object-fit: contain;
    display: block;
  }

  /* Tólspor logo is small — scale up to fill header */
  .logo-image.logo-tolspor {
    transform: scale(2);
  }

  /* Golfspor logo — scale up to crop transparent margins */
  .logo-image.logo-golfspor {
    width: 100%;
    max-width: 100%;
    transform: scale(1.8);
  }

  /* Mobile: tighten header and keep logo within card bounds */
  @media (max-width: 767.98px) {
    .header-container {
      min-height: 140px;
      padding: 16px;
    }
  }

  /* Input field tap target: increase padding so the full area between
     the label and the underline is tappable, without changing the look */
  :deep(.input-group-static .form-control) {
    padding-top: 12px;
    padding-bottom: 10px;
  }

  :deep(.form-label) {
    color: #fff !important;
  }

  :deep(.form-control) {
    color: #fff !important;
    background-color: transparent !important;
  }

  :deep(.form-control::placeholder) {
    color: rgba(255, 255, 255, 0.7) !important;
  }

  :deep(.form-control:focus) {
    color: #fff !important;
  }
</style>